import classNames from 'classnames';
import { FC, useEffect, useMemo, useState } from 'react';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SignUpContent from 'src/components/base-components/Modals/AuthModal/components/SignUpContent';
import { checkDeviceCompatibility, detectDevice, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { getSignupPage } from 'src/services/cmsApi';
import { getAuth } from 'src/services/configsApi';
import PersisterService from 'src/services/PersisterService';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setDniResult, toggleActivateFinalNextStep, toggleActivateNextStep } from 'src/store/user/actions';
import { CmsSignupPageResponse } from 'src/types/cms/cmsPageTypes';
import { ISignUpRes } from 'src/types/res-dto/authorization';
import { RootState } from 'src/types/store-types';
import { SignupSlider } from './SignupSlider';
import './styles.scss';

export const Signup: FC = () => {
  const { locale } = useI18n();
  const [signupPage, setSignupPage] = useState<CmsSignupPageResponse | null>(null);
  const isPapi = process.env.REACT_APP_WEBSITENAME === 'PapiGames';
  const [signUpModalData, setSignUpModalData] = useState<ISignUpRes>({});
  const device = detectDevice();
  const navigate = useNavigate();

  const { showMenu } = useSelector((state: RootState) => state.mobile);
  const deviceCompatibilityStatus = checkDeviceCompatibility().compatible;
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _setDniResult = useConnectedAction(setDniResult);
  const _openDialog = useConnectedAction(openDialog);
  const _toggleActivateNextStep = useConnectedAction(toggleActivateNextStep);
  const _toggleActivateFinalNextStep = useConnectedAction(toggleActivateFinalNextStep);
  const { user } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (user) navigate('/');
  }, [user]);

  const cleanSignupState = (): void => {
    _setDniResult({});
    _toggleActivateNextStep(false);
    _toggleActivateFinalNextStep(false);

    if (!deviceCompatibilityStatus) {
      _openDialog({
        dialogType: EnumDialogsKeys.DEVICE_COMPATIBILITY_POPUP,
        dialogProps: { data: {} },
      });
    }
  };

  const setContent = (action: 'open' | 'close', content: 'login' | 'registration' | 'forgot' | null): void => {
    cleanSignupState();
    const menuItem = localStorage.getItem('menuItem');
    if (PersisterService.hasPersistValue('navigation') && menuItem) {
      const res = PersisterService.getPersistValue('navigation');
      const _navItemData = res.find((f: { name: string | null }) => f.name === menuItem);
      if (_navItemData.needLogIn) {
        setBodyOverflow('unset');
        _openAuthModal(null);
        localStorage.removeItem('menuItem');
      }
    } else {
      !showMenu && setBodyOverflow(action === 'open' ? 'unset' : 'set');
      _openAuthModal(action === 'open' ? content : null);
    }
  };

  const fetchAuthData = (): void => {
    getAuth().then((res: any) => {
      if (res.success) {
        setSignUpModalData(res.result.signUp);
        sessionStorage.setItem(
          'authConfigs',
          JSON.stringify([
            res.result.signIn,
            res.result.forgotPassword,
            res.result.signUp,
            { login: res.result.login, registration: res.result.registration },
          ])
        );
      }
    });
  };

  const image = useMemo(() => {
    if (isTablet || !signupPage?.result?.content) {
      return null;
    }

    const images =
      device === 'mobile'
        ? signupPage.result.content.pageFields.bannersMobile?.map((banner) => banner.asset.path) || []
        : signupPage.result.content.pageFields.bannersDesktop?.map((banner) => banner.asset.path) || [];

    if (images.length === 0) {
      return null;
    }

    return (
      <div
        className={classNames('content_img', {
          mobile_content_img: isMobileOnly && isPapi,
        })}
      >
        <SignupSlider images={images} />
      </div>
    );
  }, [signupPage, device]);

  useEffect(() => {
    let result = null;

    if (PersisterService.hasPersistValue('authConfigs')) {
      result = PersisterService.getPersistValue('authConfigs');

      if (result[2]) setSignUpModalData(result[2]);
    } else {
      fetchAuthData();
    }
  }, []);

  useEffect(() => {
    cleanSignupState();

    return () => cleanSignupState();
  }, []);

  useEffect(() => {
    getSignupPage(locale).then((res: any) => {
      if (res.success && res.result) {
        setSignupPage(res);
      }
    });
  }, [locale]);

  return (
    <div className="signupPageRoot page_container">
      <HelmetProvider>
        <Helmet>
          <title>{signupPage?.result?.content?.meta?.pageTitle}</title>
          <meta name="description" content={signupPage?.result?.content?.meta?.pageDescription} />
          <meta name="keywords" content={signupPage?.result?.content?.meta?.pageKeywords} />
        </Helmet>
      </HelmetProvider>
      <div className="signupPageContent">
        {image !== null && <div className="signupPageImage">{image}</div>}

        <div className="signupPageForm">
          <SignUpContent
            setContent={setContent}
            forgotLink={signUpModalData?.forgotLink}
            signIn={signUpModalData?.signIn}
            registrationSteps={signUpModalData?.registrationSteps}
            styles={signUpModalData?.styles}
          />
        </div>
      </div>
    </div>
  );
};
